import { HeaderActionArea, PageLayout } from '@deere/fuel-react';
import { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { ImpersonateInput } from '../components/ImpersonateInput';
import { AdminHeader } from '../components/navigation/AdminHeader';
import {
    AdminRoutes,
    useAdminNavigationItems,
} from '../components/navigation/AdminRoutes';
import { AnchorLink } from '../components/navigation/AnchorLink';
import { SupportRequestHeaderAction } from '../components/navigation/SupportRequestHeaderAction';
import {
    WorkforceRoutes,
    useWorkforceNavigationItems,
} from '../components/navigation/WorkforceRoutes.jsx';
import { LoggedInContext } from '../context/LoggedInContext';
import { UserContext } from '../context/UserContext';

import LogoutIcon from '@mui/icons-material/Logout';
import { IconButton } from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import { setLoggingOut } from '../components/navigation/logoutState';

export const Logout = () => {
    const { oktaAuth } = useOktaAuth();

    const logout = async () => {
        // Mark logout as in progress before anything async runs, so SecureRoute
        // doesn't re-authenticate when clearing the tokens flips authState to
        // unauthenticated (which would race this redirect and silently log the
        // user back in via the master SSO session).
        setLoggingOut(true);
        // oktaAuth.signOut() only ends the per-app OAuth session; the master Deere org
        // SSO session at sso.johndeere.com persists and silently re-logs the
        // user in. So we revoke + clear tokens locally, then redirect to the
        // org-level signout URL, which is what actually terminates the session.
        const signoutUrl =
            process.env.REACT_APP_ENV !== 'prod'
                ? 'https://sso-dev.johndeere.com/login/signout'
                : 'https://sso.johndeere.com/login/signout';
        try {
            await oktaAuth.revokeAccessToken();
            await oktaAuth.revokeRefreshToken();
            oktaAuth.stop();
            await oktaAuth.tokenManager.clear();
            await oktaAuth.clearStorage();
        } catch (err) {
            // Ignore cleanup failures; the redirect below still ends the session.
        } finally {
            // Always redirect so logout is reliable even if cleanup throws.
            window.location.href = signoutUrl;
        }
    };

    return (
        <>
            <span hidden>Version 2023.10.23.1</span>
            <HeaderActionArea>
                <IconButton aria-label="Logout" onClick={logout}>
                    <LogoutIcon />
                </IconButton>
            </HeaderActionArea>
        </>
    );
};

export const AuthorizedPageLayout = ({ impersonateCallback }) => {
    const adminNavItems = useAdminNavigationItems();
    const workforceNavItems = useWorkforceNavigationItems();
    const { authState, impersonatedId, impersonatedIsAdmin, loading } =
        useContext(LoggedInContext);

    const isAdmin =
        authState.isAuthenticated &&
        authState.idToken.claims.JobBidAdminADGroups.length > 0 &&
        (impersonatedId ? impersonatedIsAdmin : true); //can be used for testing

    const isSuperAdmin =
        authState.isAuthenticated &&
        authState.idToken.claims.JobBidAdminADGroups.length > 0 &&
        (authState.idToken.claims.JobBidAdminADGroups.includes(
            'Global IT HR Labor Relations'
        ) ||
            authState.idToken.claims.JobBidAdminADGroups.includes(
                'JobBidAdmin-CorpLR'
            ));

    const impersonate = (impId, impIsAdmin) => {
        impersonateCallback(impId, impIsAdmin);
        return false;
    };

    const navigationItems = isAdmin ? adminNavItems : workforceNavItems;

    return (
        !loading && (
            <UserContext isAdmin={isAdmin} impersonatedId={impersonatedId}>
                <PageLayout
                    HeaderProps={{
                        HeaderLogoLinkComponent: AnchorLink, // <--- 💬 if you want to use react router
                        isContained: true,
                        logoLink: '/',
                        navigationItems,
                        title: 'Job Bids',
                        titleLink: '/',
                    }}
                    HeaderActionAreaComponent={
                        <>
                            {isAdmin ? <AdminHeader /> : <></>}
                            <SupportRequestHeaderAction />
                            <Logout />
                        </>
                    }
                >
                    <Helmet>
                        <title>Job Bids</title>
                    </Helmet>
                    {isSuperAdmin && (
                        <ImpersonateInput
                            callback={impersonate}
                            impersonatedId={impersonatedId}
                            impersonatedIsAdmin={impersonatedIsAdmin}
                        />
                    )}
                    {isAdmin ? <AdminRoutes /> : <WorkforceRoutes />}
                </PageLayout>
            </UserContext>
        )
    );
};
